/**
 * PythonExecutor - Executes Python code using Skulpt
 * 
 * Skulpt is a JavaScript implementation of Python that runs in the browser.
 * This provides instant code execution without server roundtrips.
 */

class PythonExecutor {
  /**
   * Execute Python code using Skulpt
   * 
   * @param {string} code - The Python code to execute
   * @param {string} stdin - Input to provide (newline separated)
   * @param {number} timeout - Maximum execution time in ms
   * @returns {Promise<{success: boolean, output: string, error: string|null, executionTime: number}>}
   */
  static async execute(code, stdin = '', timeout = 5000) {
    return new Promise((resolve) => {
      // Check if Skulpt is loaded
      if (!window.Sk) {
        resolve({
          success: false,
          output: '',
          error: 'Python interpreter not loaded. Please refresh the page.',
          executionTime: 0
        });
        return;
      }

      let output = '';
      let inputLines = stdin.split('\n');
      let inputIndex = 0;
      const startTime = Date.now();
      let timedOut = false;

      // Configure Skulpt
      window.Sk.configure({
        // Output function - called for each print statement
        output: (text) => {
          output += text;
        },
        
        // Read function - for importing modules
        read: (filename) => {
          if (window.Sk.builtinFiles && window.Sk.builtinFiles.files[filename]) {
            return window.Sk.builtinFiles.files[filename];
          }
          throw new Error(`File not found: '${filename}'`);
        },
        
        // Input function - for input() calls
        inputfun: () => {
          if (inputIndex < inputLines.length) {
            return inputLines[inputIndex++];
          }
          return '';
        },
        
        // Use Python 3 semantics
        __future__: window.Sk.python3,
        
        // Execution limit (number of operations)
        execLimit: timeout * 10
      });

      // Set up timeout
      const timeoutId = setTimeout(() => {
        timedOut = true;
        resolve({
          success: false,
          output: output,
          error: `Execution timed out after ${timeout}ms. Your code might have an infinite loop.`,
          executionTime: timeout
        });
      }, timeout);

      // Execute the code
      window.Sk.misceval.asyncToPromise(() => {
        return window.Sk.importMainWithBody('<stdin>', false, code, true);
      })
      .then(() => {
        if (!timedOut) {
          clearTimeout(timeoutId);
          resolve({
            success: true,
            output: output,
            error: null,
            executionTime: Date.now() - startTime
          });
        }
      })
      .catch((err) => {
        if (!timedOut) {
          clearTimeout(timeoutId);
          
          // Parse Skulpt error for user-friendly message
          const errorMessage = this.parseError(err);
          
          resolve({
            success: false,
            output: output,
            error: errorMessage,
            executionTime: Date.now() - startTime
          });
        }
      });
    });
  }

  /**
   * Parse Skulpt error into user-friendly message
   */
  static parseError(err) {
    if (!err) return 'An unknown error occurred';
    
    const errString = err.toString();
    
    // Extract error type and message
    const match = errString.match(/^(\w+Error)(?:\s+on line (\d+))?:\s*(.*)$/m);
    if (match) {
      const [, errorType, lineNum, message] = match;
      let friendlyMessage = `${errorType}: ${message}`;
      if (lineNum) {
        friendlyMessage += ` (line ${lineNum})`;
      }
      return friendlyMessage;
    }
    
    // Handle specific error types
    if (errString.includes('SyntaxError')) {
      return this.parseSyntaxError(errString);
    }
    
    if (errString.includes('NameError')) {
      return this.parseNameError(errString);
    }
    
    if (errString.includes('TypeError')) {
      return this.parseTypeError(errString);
    }
    
    if (errString.includes('IndentationError')) {
      return 'IndentationError: Check your indentation. Python requires consistent spacing.';
    }
    
    return errString;
  }

  /**
   * Parse syntax errors
   */
  static parseSyntaxError(errString) {
    if (errString.includes('unexpected EOF')) {
      return 'SyntaxError: Unexpected end of code. You might be missing a closing bracket, quote, or colon.';
    }
    if (errString.includes('invalid syntax')) {
      return 'SyntaxError: Invalid syntax. Check for typos, missing colons, or mismatched brackets.';
    }
    return 'SyntaxError: There\'s something wrong with how your code is written. Check for typos!';
  }

  /**
   * Parse name errors
   */
  static parseNameError(errString) {
    const match = errString.match(/name '(\w+)' is not defined/);
    if (match) {
      return `NameError: '${match[1]}' is not defined. Did you forget to create this variable or did you misspell it?`;
    }
    return 'NameError: You\'re trying to use a variable that doesn\'t exist.';
  }

  /**
   * Parse type errors
   */
  static parseTypeError(errString) {
    if (errString.includes('unsupported operand')) {
      return 'TypeError: You\'re trying to do an operation with incompatible types (like adding a number to text).';
    }
    if (errString.includes('not callable')) {
      return 'TypeError: You\'re trying to call something that isn\'t a function.';
    }
    return 'TypeError: You\'re using a value in a way it\'s not meant to be used.';
  }

  /**
   * Check if Skulpt is loaded and ready
   */
  static isReady() {
    return typeof window.Sk !== 'undefined' && 
           typeof window.Sk.importMainWithBody === 'function';
  }

  /**
   * Load Skulpt from CDN if not already loaded
   */
  static async loadSkulpt() {
    if (this.isReady()) {
      return true;
    }

    return new Promise((resolve, reject) => {
      const loadScript = (src) => {
        return new Promise((res, rej) => {
          const script = document.createElement('script');
          script.src = src;
          script.onload = () => res();
          script.onerror = () => rej(new Error(`Failed to load ${src}`));
          document.head.appendChild(script);
        });
      };

      const cdnBase = 'https://cdn.jsdelivr.net/npm/skulpt@1.2.0/dist/';
      
      loadScript(cdnBase + 'skulpt.min.js')
        .then(() => loadScript(cdnBase + 'skulpt-stdlib.js'))
        .then(() => resolve(true))
        .catch((err) => {
          console.error('Failed to load Skulpt:', err);
          reject(err);
        });
    });
  }
}

export default PythonExecutor;